import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {apiClient} from '../../../restClient'
import {useAppStore} from '../../../store/appStore'
import {PerPageOneHundred} from '../../../constants/perPageConstants'
import {
  BULK_CREATE_TASKS_ENDPOINT,
  CREATE_TASK_ENDPOINT,
  DELETE_TASKS_ENDPOINT,
  GET_TASKS_ENDPOINT,
  UPDATE_TASK_BLOCK_ENDPOINT,
  UPDATE_TASK_ENDPOINT,
  UPDATE_TASK_STATUS_ENDPOINT,
} from '../../../api/endPoints'
import type {ITask} from '../../../types'
import {initiativeActivityApi} from '../../../api/initiativeActivityApi'
import {dateUtil} from '../../../utils/dateUtil'
import {TASK_STATUS} from '../constants/taskConstants'

/** Shape of the `GET /tasks` payload — `{tasks, count}`. */
interface ITasksPage {
  tasks: ITask[]
  count?: number
}

/** Every cached `['tasks', ...]` page, paired with its key, so an optimistic
 *  write can be rolled back exactly as it was found. */
type TTasksSnapshots = [readonly unknown[], ITasksPage | undefined][]

interface IToggleTaskBlockVariables {
  taskID: string
  isBlocked: boolean
  /** Required by the block flow's UI when blocking; ignored on unblock. */
  blockedReason?: string
}

interface IFetchTasksParams {
  initID: string
  page: number
  search?: string
  sortBy?: string
  orderBy?: string
}

const fetchTasks = async (params: IFetchTasksParams): Promise<ITasksPage> => {
  const {initID, page, search, sortBy, orderBy} = params
  const queryParams: Record<string, string | number> = {
    id: initID,
    limit: PerPageOneHundred,
    offset: (page - 1) * PerPageOneHundred,
  }
  if (search) queryParams['filterInput.search'] = search
  if (sortBy && orderBy) {
    queryParams['filterInput.sortInput.sortBy'] = sortBy
    queryParams['filterInput.sortInput.orderBy'] = orderBy
  }
  return apiClient
    .get(GET_TASKS_ENDPOINT, {params: queryParams})
    .then(r => r.data)
}

export const tasksApi = {
  useTasks: (
    initID: string,
    page: number,
    search?: string,
    sortBy?: string,
    orderBy?: string,
  ) =>
    useQuery({
      queryKey: ['tasks', initID, page, search, sortBy, orderBy],
      queryFn: () => fetchTasks({initID, page, search, sortBy, orderBy}),
      enabled: !!initID,
      placeholderData: keepPreviousData,
      // Two components observe this: the initiative header (for the completion
      // progress and the weekly review) and the Actions list. Without a
      // staleTime the second one to mount refetches immediately, because the
      // default marks data stale the moment it arrives — so a single page view
      // issued the same request twice. Every mutation still calls
      // invalidateQueries, which refetches active queries regardless of this,
      // so writes are as fresh as before.
      staleTime: 30_000,
    }),

  useCreateTask: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: ITask): Promise<ITask> =>
        apiClient
          .post(CREATE_TASK_ENDPOINT, {
            ...payload,
            // dueDate is a calendar day, not an instant — anchor it to UTC
            // midnight so it reads as the same day in every timezone.
            ...(payload.dueDate
              ? {dueDate: dateUtil.toUtcCalendarDay(payload.dueDate)}
              : {}),
          })
          .then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: AxiosError<{message?: string}>) => {
        const msg =
          err.response?.data?.message || err.message || 'Create task failed'
        useAppStore.getState().showError(msg)
      },
    })
  },

  useBulkCreateTasks: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        tasksNames,
        initiativeId,
        dueDate,
      }: {
        tasksNames: string[]
        initiativeId?: string
        dueDate: Date
      }): Promise<unknown> =>
        apiClient
          .post(BULK_CREATE_TASKS_ENDPOINT, {
            tasksNames,
            initiativeId,
            dueDate: dateUtil.toUtcCalendarDay(dueDate),
          })
          .then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: AxiosError<{message?: string}>) => {
        const msg =
          err.response?.data?.message || err.message || 'Bulk create failed'
        useAppStore.getState().showError(msg)
      },
    })
  },

  useUpdateTask: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({id, task}: {id: string; task: ITask}): Promise<unknown> => {
        const payload = {
          id,
          ...task,
          responsible: task.responsible
            ? Array.isArray(task.responsible)
              ? task.responsible
              : [task.responsible]
            : undefined,
          ...(task.dueDate
            ? {dueDate: dateUtil.toUtcCalendarDay(task.dueDate)}
            : {}),
        }
        return apiClient.put(UPDATE_TASK_ENDPOINT, payload).then(r => r.data)
      },
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: AxiosError<{message?: string}>) => {
        const msg =
          err.response?.data?.message || err.message || 'Update task failed'
        useAppStore.getState().showError(msg)
      },
    })
  },

  useDeleteTask: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({ids}: {ids: string[]}): Promise<unknown> =>
        apiClient
          .delete(DELETE_TASKS_ENDPOINT, {data: {ids}})
          .then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: AxiosError<{message?: string}>) => {
        const msg =
          err.response?.data?.message || err.message || 'Delete task failed'
        useAppStore.getState().showError(msg)
      },
    })
  },

  useChangeTaskStatus: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        taskID,
        status,
      }: {
        taskID: string
        status: string
      }): Promise<unknown> =>
        apiClient
          .put(UPDATE_TASK_STATUS_ENDPOINT, {id: taskID, status})
          .then(r => r.data),
      // A status pill has to answer the click before the round-trip, so the
      // cached pages are patched here and restored in onError. This is why the
      // list holds no local override map of its own — one that never cleared on
      // success used to shadow server data for the screen's lifetime.
      onMutate: async ({
        taskID,
        status,
      }: {
        taskID: string
        status: string
      }): Promise<{snapshots: TTasksSnapshots}> => {
        await qc.cancelQueries({queryKey: ['tasks']})
        const snapshots = qc.getQueriesData<ITasksPage>({queryKey: ['tasks']})
        snapshots.forEach(([key, page]) => {
          if (!page?.tasks) return
          qc.setQueryData<ITasksPage>(key, {
            ...page,
            tasks: page.tasks.map(task =>
              String(task.id) === taskID
                ? {
                    ...task,
                    status,
                    // Mirrors the server: completed_at is stamped on the way
                    // into Completed and never cleared on the way out.
                    ...(status === TASK_STATUS.completed
                      ? {completed_at: new Date()}
                      : {}),
                  }
                : task,
            ),
          })
        })
        return {snapshots}
      },
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (
        err: AxiosError<{message?: string}>,
        _variables,
        context: {snapshots: TTasksSnapshots} | undefined,
      ) => {
        context?.snapshots.forEach(([key, page]) => {
          qc.setQueryData(key, page)
        })
        const msg =
          err.response?.data?.message || err.message || 'Status change failed'
        useAppStore.getState().showError(msg)
      },
    })
  },

  useToggleTaskBlock: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        taskID,
        isBlocked,
        blockedReason,
      }: IToggleTaskBlockVariables): Promise<unknown> =>
        apiClient
          .put(UPDATE_TASK_BLOCK_ENDPOINT, {
            id: taskID,
            isBlocked,
            // A reason only means something while blocked; the server clears
            // it on unblock.
            ...(isBlocked ? {blockedReason} : {}),
          })
          .then(r => r.data),
      // Same optimistic patch-and-restore as useChangeTaskStatus, so the chip
      // and alert answer the click before the round-trip.
      onMutate: async ({
        taskID,
        isBlocked,
        blockedReason,
      }: IToggleTaskBlockVariables): Promise<{snapshots: TTasksSnapshots}> => {
        await qc.cancelQueries({queryKey: ['tasks']})
        const snapshots = qc.getQueriesData<ITasksPage>({queryKey: ['tasks']})
        snapshots.forEach(([key, page]) => {
          if (!page?.tasks) return
          qc.setQueryData<ITasksPage>(key, {
            ...page,
            tasks: page.tasks.map(task =>
              String(task.id) === taskID
                ? {
                    ...task,
                    isBlocked,
                    blockedReason: isBlocked ? (blockedReason ?? null) : null,
                  }
                : task,
            ),
          })
        })
        return {snapshots}
      },
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['tasks']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userTasks']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void initiativeActivityApi.refresh(qc)
      },
      onError: (
        err: AxiosError<{message?: string}>,
        _variables,
        context: {snapshots: TTasksSnapshots} | undefined,
      ) => {
        context?.snapshots.forEach(([key, page]) => {
          qc.setQueryData(key, page)
        })
        const msg =
          err.response?.data?.message || err.message || 'Block change failed'
        useAppStore.getState().showError(msg)
      },
    })
  },
} as const
